import classNames from 'classnames'
import { motion } from 'framer-motion'
import React from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { motionFadeIn } from '@apps-common/config/motion-variants'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  registration: Partial<UseFormRegister<any>>
  containerClassName?: string
  innerContainerClassName?: string
  label?: string
  labelLeft?: string
  labelRight?: string
  errors?: FieldError
  size?: 'sm' | 'lg'
  loading?: boolean
  disabled?: boolean
  ignoreCtxTheme?: boolean
}

export const ToggleInputSM = React.forwardRef<HTMLInputElement, React.PropsWithChildren<InputProps>>((props: InputProps, ref) => {
  let {
    className,
    containerClassName,
    innerContainerClassName,
    registration,
    id,
    label,
    labelLeft,
    labelRight,
    errors,
    loading = false,
    disabled = false,
    ignoreCtxTheme
  } = props

  const defaultToggleStyles = '[--tglbg:hsl(var(--rn-surface))] border-brand bg-brand/30 checked:bg-brand'
  const toggleInputClasses = useProjectHook(
    defaultToggleStyles,
    '[--tglbg:hsl(var(--rn-ctx-surface))] border-ctx-mm-brand bg-ctx-mm-brand/30 checked:bg-ctx-mm-brand'
  )
  return (
    <>
      {loading && (
        <motion.div
          key={`${id}-checkbox-sm-skeleton`}
          {...motionFadeIn(0.2)}
          className={classNames('mb-2 animate-pulse', containerClassName)}
        >
          <div className='relative flex items-center justify-between px-[10px] my-[0.5rem]'>
            <div className='bg-skeleton h-[22px] w-[60%] rounded-lg' />
            <div className='w-[48px] h-[22px] rounded-full bg-skeleton' />
          </div>
          {/* <div className='h-[10px]' /> */}
        </motion.div>
      )}
      {!loading && (
        <motion.div key={`${id}-checkbox-sm`} {...motionFadeIn(0.2)} className={classNames('mb-2', containerClassName)}>
          <div className={classNames(innerContainerClassName, 'relative flex items-center justify-between px-[10px] my-[0.5rem]')}>
            <label className={classNames('text-[0.9em] sm:text-[1em] text-text mr-[10px]', disabled && 'opacity-30')} htmlFor={id}>
              {label}
            </label>
            <div className={classNames('text-text flex items-center justify-between grow')}>
              {labelLeft && <span className='text-[0.9em] sm:text-[1em] text-text  mr-[5px]'>{labelLeft}</span>}
              <input
                id={id}
                type='checkbox'
                className={classNames(
                  'toggle border disabled:bg-secondary/10',
                  ignoreCtxTheme && defaultToggleStyles,
                  !ignoreCtxTheme && toggleInputClasses,
                  className
                )}
                disabled={disabled}
                {...registration}
              />
              {labelRight && <span className='ml-[5px]'>{labelRight}</span>}
            </div>
          </div>
          <p className='pl-2 pt-2 text-error text-xs'>{errors?.message}</p>
        </motion.div>
      )}
    </>
  )
})

ToggleInputSM.displayName = 'ToggleInputSM'
